import { createRouter, createRoute, createRootRoute, Outlet } from '@tanstack/react-router'
import { LoginPage } from '../features/auth/LoginPage'
import { AzureCallbackPage } from '../features/auth/AzureCallbackPage'
import { NotFoundPage } from '../features/auth/NotFoundPage'
import { SecurityPolicyPage } from '../features/security/SecurityPolicyPage'
import { PrivacyPolicyPage } from '../features/security/PrivacyPolicyPage'
import { DashboardPage } from '../features/dashboard/DashboardPage'
import { ScanListPage } from '../features/scan/ScanListPage'
import { ScanDetailPage } from '../features/scan/ScanDetailPage'
import { AdminOverviewPage } from '../features/admin/AdminOverviewPage'
import { AdminWorkersPage } from '../features/admin/AdminWorkersPage'
import { AdminUsersPage } from '../features/admin/AdminUsersPage'
import { AdminAuditLogsPage } from '../features/admin/AdminAuditLogsPage'
import { AdminPracticeCasesPage } from '../features/admin/AdminPracticeCasesPage'
import { IntegrationManagementPage } from '../features/admin/IntegrationManagementPage'
import { IndexedFoldersManagementPage } from '../features/admin/pages/IndexedFoldersManagementPage'
import RolesListPage from '../features/admin/rbac/RolesListPage'
import RoleDetailPage from '../features/admin/rbac/RoleDetailPage'
import PermissionsPage from '../features/admin/rbac/PermissionsPage'
import { ProfilePage } from '../features/profile/ProfilePage'
import { CrisisSelectPage } from '../features/crisis/components/CrisisSelectPage'
import { WritingAssistantListPage } from '../features/writing/WritingAssistantListPage'
import { BobAnalyseListPage } from '../features/bob-analyse/components/BobAnalyseListPage'
import { ContactsListPage } from '../features/contacts/ContactsListPage'
import { PlanvormingPage } from '../features/documents/PlanvormingPage'
import { ProtectedRoute } from '../components/auth/ProtectedRoute'
import { PermissionGuard } from '../components/auth/PermissionGuard'

// Route config dictionary with protection info
const routeConfigs = [
  { path: '/', component: DashboardPage, protected: true, requiredPermissions: [] },
  { path: '/dashboard', component: DashboardPage, protected: true, requiredPermissions: [] },
  { path: '/login', component: LoginPage, protected: false, requiredPermissions: [] },
  { path: '/auth/azure/callback', component: AzureCallbackPage, protected: false, requiredPermissions: [] },
  { path: '/security-policy', component: SecurityPolicyPage, protected: false, requiredPermissions: [] },
  { path: '/privacy-policy', component: PrivacyPolicyPage, protected: false, requiredPermissions: [] },
  {
    path: '/profile',
    component: ProfilePage,
    protected: true,
    requiredPermissions: ['profile.update'],
  },
  {
    path: '/crisis/select',
    component: CrisisSelectPage,
    protected: true,
    requiredPermissions: ['crisis.list'],
  },
  { path: '/scan', component: ScanListPage, protected: true, requiredPermissions: ['scans.list'] },
  {
    path: '/scan/detail/$id',
    component: ScanDetailPage,
    protected: true,
    requiredPermissions: ['scans.read'],
  },
  {
    path: '/writing',
    component: WritingAssistantListPage,
    protected: true,
    requiredPermissions: ['writing.list'],
  },
  {
    path: '/bob',
    component: BobAnalyseListPage,
    protected: true,
    requiredPermissions: ['bob.list'],
  },
  {
    path: '/contacts',
    component: ContactsListPage,
    protected: true,
    requiredPermissions: ['contacts.list'],
  },
  {
    path: '/planvorming',
    component: PlanvormingPage,
    protected: true,
    requiredPermissions: ['documents.browse'],
  },
  {
    path: '/admin',
    component: AdminOverviewPage,
    protected: true,
    requiredPermissions: ['users.list', 'audit.list', 'system.settings.view', 'practice.list', 'integration.list', 'rbac.roles.list'],
  },
  {
    path: '/admin/workers',
    component: AdminWorkersPage,
    protected: true,
    requiredPermissions: ['system.settings.view'],
  },
  {
    path: '/admin/users',
    component: AdminUsersPage,
    protected: true,
    requiredPermissions: ['users.list'],
  },
  {
    path: '/admin/audit-logs',
    component: AdminAuditLogsPage,
    protected: true,
    requiredPermissions: ['audit.list'],
  },
  {
    path: '/admin/practice-cases',
    component: AdminPracticeCasesPage,
    protected: true,
    requiredPermissions: ['practice.list'],
  },
  {
    path: '/admin/rbac/roles',
    component: RolesListPage,
    protected: true,
    requiredPermissions: ['rbac.roles.list'],
  },
  {
    path: '/admin/rbac/roles/$roleId',
    component: RoleDetailPage,
    protected: true,
    requiredPermissions: ['rbac.roles.view'],
  },
  {
    path: '/admin/rbac/permissions',
    component: PermissionsPage,
    protected: true,
    requiredPermissions: ['rbac.permissions.view'],
  },
  {
    path: '/admin/integrations',
    component: IntegrationManagementPage,
    protected: true,
    requiredPermissions: ['integration.list'],
  },
  {
    path: '/admin/indexed-folders',
    component: IndexedFoldersManagementPage,
    protected: true,
    requiredPermissions: ['indexed-folders.manage'],
  },
  { path: '$', component: NotFoundPage, protected: false, requiredPermissions: [] },
]

const rootRoute = createRootRoute({
  component: () => (
    <div className="min-h-screen bg-background">
      <Outlet />
    </div>
  ),
})

// Dynamically create routes from config
const routes = routeConfigs.map(cfg => {
  const Component = cfg.component
  let WrappedComponent = Component

  // Wrap with PermissionGuard if permissions are required
  if (cfg.requiredPermissions && cfg.requiredPermissions.length > 0) {
    const PermissionWrapped = () => (
      <PermissionGuard requiredPermissions={cfg.requiredPermissions}>
        <Component />
      </PermissionGuard>
    )
    WrappedComponent = PermissionWrapped
  }

  // Wrap with ProtectedRoute if authentication is required
  if (cfg.protected) {
    const OriginalComponent = WrappedComponent
    const AuthWrapped = () => (
      <ProtectedRoute>
        <OriginalComponent />
      </ProtectedRoute>
    )
    WrappedComponent = AuthWrapped
  }

  const routeConfig: Record<string, unknown> = {
    getParentRoute: () => rootRoute,
    path: cfg.path,
    component: WrappedComponent,
  }

  // Add search parameters for routes that need selectedId
  if (cfg.path === '/bob' || cfg.path === '/writing') {
    Object.assign(routeConfig, {
      validateSearch: (search: Record<string, unknown>) => ({
        selectedId: search.selectedId as string | undefined,
      }),
    })
  }

  // Add search parameters for contacts route to support tab selection
  if (cfg.path === '/contacts') {
    Object.assign(routeConfig, {
      validateSearch: (search: Record<string, unknown>) => ({
        tab: search.tab as string | undefined,
      }),
    })
  }

  return createRoute(routeConfig as unknown as Parameters<typeof createRoute>[0])
})

const routeTree = rootRoute.addChildren(routes)

// Create a new router instance
export const router = createRouter({ routeTree })

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
